<template>
	<div class="list-container column">
		<div class="title-container">
			<div class="title">{{ $t('roundchecklist.title') }}</div>
			<TabComponent
				class="tabs"
				:class="{ mobile: isMobile }"
				:type="'is-toggle'"
				:tabs="tabs"
				v-model="selectedTab"
			/>
			<div class="dash-select" :class="{ mobile: isMobile }">
				<v-select
					class="panel-select dashboard"
					:value="selectedChecklist"
					:options="checklists"
					:placeholder="$t('roundchecklist.select_placeholder')"
					:allowEmpty="false"
					:internalSearch="true"
					:showLabels="false"
					@select="selectChecklist"
					label="name"
					track-by="id"
				>
					<span slot="noResult">{{ $t('roundchecklist.no_results') }}</span>
					<span
						slot="caret"
						slot-scope="{ toggle }"
						class="caret"
						@mousedown.prevent.stop="toggle()"
					><i class="fas fa-caret-down caret-inner"></i
					></span>
				</v-select>
			</div>
		</div>
		<div
			class="category-container"
			v-for="(question, index) in comQuestions"
			:key="index"
		>
			<p
				class="category"
				:class="{ 'is-first': index === 0 }"
				v-if="
					question.groupName !==
						comQuestions[index > 0 ? index - 1 : index].groupName || index === 0
				"
			>
				{{ question.groupName }}
			</p>
			<div class="question-container">
				<div class="question-description" :class="{ mobile: isMobile }">
					<p>{{ question.name || question.description }}</p>
					<div class="lawlink" v-if="question.lawLink">
						{{ $t('roundchecklist.label_reference') }}:
						<a :href="getFormattedLawLink(question.lawLink)" target="_blank">{{
							question.lawLink
						}}</a>
					</div>
				</div>
				<div
					class="question-info"
					:class="{ mobile: isMobile }"
					v-if="question.description"
				>
					<p>{{ $t('roundchecklist.label_info') }}</p>
					<p>{{ question.description }}</p>
				</div>
				<div class="question-buttons" :class="{ mobile: isMobile }">
					<button
						class="button is-success"
						@click="positive(question)"
						:class="question.answer === question.positive ? 'active' : ''"
					>
						{{ question.positive }}
					</button>
					<button
						class="button is-danger"
						@click="toggleModal(question)"
						:class="question.answer === question.negative ? 'active' : ''"
					>
						{{ question.negative }}
					</button>
					<button
						class="button"
						:class="question.answer === '' ? 'is-link' : ''"
						@click="irrelevant(question)"
					>
						{{ $t('roundchecklist.button_irrelevant') }}
					</button>
				</div>
			</div>
		</div>
		<div class="panel-list no-hover" v-if="comQuestions.length == 0">
			<p class="panel-list-item">
				 {{ selectedTab ? $t('roundchecklist.no_answered_questions') : $t('roundchecklist.no_unanswered_questions') }}
			</p>
		</div>
		<QuestionModal
			v-if="showModal"
			:question="selectedQuestion"
			@save="saveNegative"
			@cancel="toggleModal"
		/>
	</div>
</template>

<script>
import TabComponent from '@/components/TabComponent';
import QuestionModal from '../modals/QuestionModal';
import { mapGetters } from 'vuex';
export default {
	components: {
		TabComponent,
		QuestionModal
	},
	props: {
		checklists: Array
	},
	data: () => ({
		selectedChecklist: {},
		selectedIndex: 0,
		selectedTab: 0,
		tabs: [
			{
				name: this.$t('roundchecklist.tab_unanswered'),
				icon: 'fa-clipboard-list'
			},
			{
				name: this.$t('roundchecklist.tab_answered'),
				icon: 'fa-clipboard-check'
			}
		],
		showModal: false,
		selectedQuestion: {}
	}),
	computed: {
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('user', ['getUser']),
		comQuestions() {
			let list = this.selectedChecklist.roundHistoryQuestions;
			list.sort((a, b) =>
				a.groupName > b.groupName
					? 1
					: a.groupName === b.groupName
						? a.id > b.id
							? 1
							: -1
						: -1
			);
			let filteredList = [];

			if (this.selectedTab === 0) {
				list.forEach(question => {
					if (question.answer === null) filteredList.push(question);
				});
			} else {
				list.forEach(question => {
					if (question.answer !== null) filteredList.push(question);
				});
			}

			return filteredList;
		}
	},
	watch: {
		checklists: {
			handler() {
				this.selectedChecklist = this.checklists[this.selectedIndex];
			}
		}
	},
	methods: {
		selectChecklist(list) {
			this.selectedIndex = this.checklists.findIndex(
				element => element.id === list.id
			);
			this.selectedChecklist = this.checklists[this.selectedIndex];
		},
		positive(question) {
			question.answer = question.positive;
			question.userName = this.getUser.email;
			this.$emit('saveState');
		},
		saveNegative() {
			this.selectedQuestion.answer = this.selectedQuestion.negative;
			this.selectedQuestion.userName = this.getUser.email;
			this.toggleModal();
			this.$emit('saveState');
		},
		irrelevant(question) {
			question.answer = '';
			question.userName = this.getUser.email;
			this.$emit('saveState');
		},
		toggleModal(question) {
			if (question) {
				this.selectedQuestion = question;
			}

			this.showModal = !this.showModal;
		},
		getFormattedLawLink(lawLink) {
			if (lawLink.toUpperCase().startsWith('HTTP')) return lawLink;

			return `https://${lawLink}`;
		}
	},
	created() {
		this.selectedChecklist = this.checklists[this.selectedIndex];
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";
.title-container {
  display: flex;
  flex-wrap: wrap;
}
.dash-select {
  margin-top: 0.05rem;
  margin-top: -0.5rem;
  &.mobile {
    margin-right: 0.5rem;
    margin-left: auto;
    margin-top: -0.5rem;
    margin-bottom: 0.5rem;
  }
}
.lawlink {
  margin-top: 1rem;
  word-break: break-word;
}

p.category {
  border-top: 1px solid #d3d3d35d;
  &.is-first {
    border: none;
  }
  font-weight: 600;
}
.category-container {
  border-bottom: 1px solid #d3d3d35d;
  &:last-child {
    border: none;
  }
}
.question-container {
  padding: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  .question-info {
    margin-left: auto;
  }
  .question-description,
  .question-info {
    max-width: 50%;
    min-width: 50%;
    margin-bottom: 0.5rem;
    &.mobile {
      padding: 10px 0;
      min-width: 100%;
      max-width: 100%;
    }
    a {
      color: #4a87d8;
    }
  }
  .question-description {
    p {
      font-weight: 500;
    }
  }
  .question-info {
    font-size: 15px;
  }
  .question-buttons {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    &.mobile {
      justify-content: space-between;
    }
    .button {
      min-width: 80px;
      margin: 0 0.25rem;
      &.is-success {
        background-color: #02aa3d;
      }
      &:not(.active) {
        &.is-success {
          background-color: #7eca99;
        }
        &.is-danger {
          background-color: #e98787;
        }
      }
      &:first-child {
        margin-left: 0;
      }
      &:last-child {
        margin-right: 0;
      }
    }
  }
}

.tabs {
  margin-left: auto;
  margin-right: 0.5rem;
  margin-top: -0.55rem;
  &.mobile {
    margin-top: -0.5rem;
  }
}
</style>

<style lang="scss">
.multiselect.panel-select.dashboard {
  min-width: 220px;
  max-width: 220px;
  .caret {
    background-color: #fcfcfc;
  }
  .multiselect__tags {
    background-color: white;

    .multiselect__single,
    .multiselect__input {
      background-color: white;
    }
  }
  .multiselect__content {
    background: white;
  }
}
</style>
